import {type RefObject, useCallback, useEffect, useRef} from 'react';
import {type ReactZoomPanPinchRef, type ReactZoomPanPinchState} from 'react-zoom-pan-pinch';
import {measureVisibleCropRect} from '../../libs/resolveCropRect';
import {type StepViewportUpdate} from './types';

type TransformState = Pick<ReactZoomPanPinchState, 'scale' | 'positionX' | 'positionY'>;

const TRANSFORM_IDLE_MS = 100;

export const useViewportUpdate = (
  frameRef: RefObject<HTMLElement | null>,
  imageRef: RefObject<HTMLImageElement | null>,
  onUpdate?: (update: StepViewportUpdate) => void,
) => {
  const isUserTransform = useRef(false);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const emitUpdate = useCallback(
    (state: TransformState) => {
      const frame = frameRef.current;
      const image = imageRef.current;
      if (!onUpdate || !frame || !image) return;

      const viewport = measureVisibleCropRect(frame, image);
      if (!viewport) return;

      onUpdate({
        zoom: state.scale,
        pan: {
          x: state.positionX,
          y: state.positionY,
          frameWidth: frame.clientWidth,
          frameHeight: frame.clientHeight,
        },
        viewport,
      });
    },
    [frameRef, imageRef, onUpdate],
  );

  const finishUserTransform = useCallback(
    (state: TransformState) => {
      if (!isUserTransform.current) return;

      clearTimeout(idleTimer.current);
      isUserTransform.current = false;
      emitUpdate(state);
    },
    [emitUpdate],
  );

  const startUserTransform = useCallback(() => {
    isUserTransform.current = Boolean(onUpdate);
  }, [onUpdate]);

  // Programmatic controls do not emit onZoomStop, so finish after their animation becomes idle.
  const onTransformed = useCallback(
    (_ref: ReactZoomPanPinchRef, state: TransformState) => {
      if (!isUserTransform.current) return;

      clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => finishUserTransform(state), TRANSFORM_IDLE_MS);
    },
    [finishUserTransform],
  );

  useEffect(
    () => () => {
      clearTimeout(idleTimer.current);
    },
    [],
  );

  return {finishUserTransform, onTransformed, startUserTransform};
};
